
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { EcommerceSettings } from '@/components/admin/campaigns/types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface EcommerceRewardsTabProps {
  settings: EcommerceSettings;
  updateSettings: (settings: Partial<EcommerceSettings>) => void;
}

const EcommerceRewardsTab: React.FC<EcommerceRewardsTabProps> = ({
  settings,
  updateSettings,
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Rewards Configuration</CardTitle>
        <CardDescription>
          Set up how rewards are given to customers in your referral program
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <Label>Reward Type</Label>
          <RadioGroup 
            value={settings.rewardType} 
            onValueChange={(value) => updateSettings({ 
              rewardType: value as EcommerceSettings['rewardType'] 
            })}
            className="flex flex-col space-y-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="fixed" id="fixed" />
              <Label htmlFor="fixed" className="cursor-pointer">Fixed Amount Discount</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="percentage" id="percentage" />
              <Label htmlFor="percentage" className="cursor-pointer">Percentage Discount</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="free_shipping" id="free_shipping" />
              <Label htmlFor="free_shipping" className="cursor-pointer">Free Shipping</Label>
            </div>
          </RadioGroup>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="rewardValue">Reward Value</Label>
            <Input
              id="rewardValue"
              type="number"
              value={settings.rewardValue}
              onChange={(e) => updateSettings({ rewardValue: Number(e.target.value) })}
              placeholder={settings.rewardType === 'percentage' ? '10' : '10.00'}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="rewardCurrency">Currency</Label>
            <Select 
              value={settings.rewardCurrency} 
              onValueChange={(value) => updateSettings({ rewardCurrency: value })}
            >
              <SelectTrigger id="rewardCurrency">
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD ($)</SelectItem>
                <SelectItem value="EUR">EUR (€)</SelectItem>
                <SelectItem value="GBP">GBP (£)</SelectItem>
                <SelectItem value="CAD">CAD (C$)</SelectItem>
                <SelectItem value="AUD">AUD (A$)</SelectItem>
                <SelectItem value="JPY">JPY (¥)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="auto-reward">Automatic Reward Distribution</Label>
            <p className="text-sm text-muted-foreground">
              Automatically send rewards when a referral is completed
            </p>
          </div>
          <Switch 
            id="auto-reward"
            checked={settings.autoRewardEnabled}
            onCheckedChange={(checked) => updateSettings({ autoRewardEnabled: checked })}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default EcommerceRewardsTab;
